import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/base/Toast';

interface PaymentItem {
  id: string;
  orderId: string;
  product: string;
  amount: number;
  statusCode: string;
  createTime: string;
  expireTime: string | null;
  paymentMethod: string | null;
  orderName: string;
  weight: number;
  marginPaid: number;
  direction: string;
  lockPrice: number; // 锁价金价
  totalAmount: number; // 预约总额
}

const statusMap: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  pending: {
    label: '待支付',
    color: 'text-gold-700',
    bg: 'bg-gold-50 border-gold-200',
    icon: 'ri-time-fill',
  },
};

const tabs = [
  { key: 'pending', label: '待支付' },
];

function useCountUp(target: number, duration = 800) {
  const [value, setValue] = useState(0);
  const startRef = useRef<number | null>(null);
  const fromRef = useRef(0);
  const toRef = useRef(target);

  useEffect(() => {
    fromRef.current = value;
    toRef.current = target;
    startRef.current = null;

    const animate = (timestamp: number) => {
      if (startRef.current === null) startRef.current = timestamp;
      const progress = Math.min((timestamp - startRef.current) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(fromRef.current + (toRef.current - fromRef.current) * eased));
      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [target, duration]);

  return value;
}

export default function PaymentPage() {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedPayment, setSelectedPayment] = useState<string | null>(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentItems, setPaymentItems] = useState<PaymentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [paying, setPaying] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;

      if (!userId) {
        setPaymentItems([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const paymentsData = data || [];

      // 批量查询关联订单信息
      const orderIds = paymentsData.map((row) => row.order_id).filter(Boolean);
      let ordersMap: Record<string, {
        name: string;
        weight: number;
        current_margin: number;
        direction: string;
        open_price: number;
        total_amount: number;
      }> = {};
      if (orderIds.length > 0) {
        const { data: ordersData } = await supabase
          .from('orders')
          .select('id, name, weight, current_margin, direction, open_price, total_amount')
          .in('id', orderIds);
        (ordersData || []).forEach((o) => {
          ordersMap[o.id] = {
            name: o.name || '',
            weight: Number(o.weight) || 0,
            current_margin: Number(o.current_margin) || 0,
            direction: o.direction || 'IN',
            open_price: Number(o.open_price) || 0,
            total_amount: Number(o.total_amount) || 0,
          };
        });
      }

      setPaymentItems(
        paymentsData.map((row) => {
          const orderInfo = row.order_id ? ordersMap[row.order_id] : undefined;
          const rawProduct = row.product || '';
          const orderName = orderInfo?.name || rawProduct;
          const weight = orderInfo?.weight || 0;
          const marginPaid = orderInfo?.current_margin || 0;
          const direction = orderInfo?.direction || 'IN';
          const lockPrice = orderInfo?.open_price || 0;
          const totalAmount = orderInfo?.total_amount || 0;
          return {
            id: row.id,
            orderId: row.order_id || '',
            product: rawProduct,
            amount: Number(row.amount) || 0,
            statusCode: row.status || 'pending',
            createTime: row.create_time || '',
            expireTime: row.expire_time || null,
            paymentMethod: row.payment_method || null,
            orderName,
            weight,
            marginPaid,
            direction,
            lockPrice,
            totalAmount,
          };
        }),
      );
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePay = async () => {
    if (!selectedPayment) return;
    const target = allPaymentItems.find((p) => p.id === selectedPayment);
    const payAmount = target?.amount ?? 0;
    setPaying(true);

    try {
      // 从账户可用余额扣款（余额支付）
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const { data: summary } = await supabase
          .from('account_summary')
          .select('available_margin, used_margin, long_weight, net_weight')
          .eq('user_id', user.id)
          .maybeSingle();
        const currentAvailable = Number(summary?.available_margin) || 0;
        if (currentAvailable < payAmount) {
          showToast('可用余额不足', 'error');
          return;
        }
        await supabase
          .from('account_summary')
          .update({ available_margin: Math.max(0, currentAvailable - payAmount) })
          .eq('user_id', user.id);

        // 获取关联订单信息，用于后续创建履约记录和金库记录
        const orderId = target?.orderId;
        if (orderId) {
          const { data: orderData } = await supabase
            .from('orders')
            .select('*')
            .eq('id', orderId)
            .maybeSingle();

          if (orderData && orderData.status === 'open') {
            const now = new Date();
            const pad = (n: number) => String(n).padStart(2, '0');
            const completeTime = `${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

            // 1. 释放订单保证金，并减去对应持仓重量
            const depositAmount = Number(orderData.current_margin) || 0;
            const orderWeight = Number(orderData.weight) || 0;
            const orderDirection = orderData.direction || 'IN';
            const currentUsed = Number(summary?.used_margin) || 0;
            const currentLong = Number(summary?.long_weight) || 0;
            const currentShort = Number(summary?.short_weight) || 0;

            const newUsed = Math.max(0, currentUsed - depositAmount);
            const newAvailable = Math.max(0, currentAvailable - payAmount + depositAmount);
            const newLong = orderDirection === 'IN'
              ? Math.max(0, currentLong - orderWeight)
              : currentLong;
            const newShort = orderDirection === 'OUT'
              ? Math.max(0, currentShort - orderWeight)
              : currentShort;

            await supabase
              .from('account_summary')
              .update({
                used_margin: newUsed,
                available_margin: newAvailable,
                long_weight: newLong,
                short_weight: newShort,
                net_weight: newLong - newShort,
              })
              .eq('user_id', user.id);

            // 2. 更新订单状态为 fulfilled
            await supabase
              .from('orders')
              .update({ status: 'fulfilled' })
              .eq('id', orderId);

            // 3. 创建履约记录
            const { error: performError } = await supabase.from('perform_records').insert({
              id: `PF${Date.now()}`,
              user_id: user.id,
              order_id: orderId,
              product: orderData.name || target?.product || '',
              status: 'completed',
              lock_price: Number(orderData.open_price) || 0,
              actual_price: Number(orderData.total_amount) || 0,
              create_time: orderData.create_time || completeTime,
              complete_time: completeTime,
              warehouse: '上海黄金交割库A区',
              tracking_no: null,
            });
            if (performError) {
              console.error('履约记录创建失败', performError);
            }

            // 4. 创建金库记录
            const code = `GB${Date.now().toString(36).toUpperCase().slice(-6)}${Math.floor(Math.random() * 900 + 100)}`;
            const { error: assetError } = await supabase.from('gold_assets').insert({
              id: `GA${Date.now()}`,
              user_id: user.id,
              product: orderData.name || target?.product || '',
              code,
              weight: Number(orderData.weight) || 0,
              status: '存储中',
              store_date: completeTime.split(' ')[0],
              location: '上海黄金交割库A区',
            });
            if (assetError) {
              console.error('金库记录创建失败', assetError);
            }

            // 5. 更新用户 profile 持有黄金数量
            const { data: profile } = await supabase.from('profiles').select('holding_gold').eq('id', user.id).maybeSingle();
            await supabase
              .from('profiles')
              .update({ holding_gold: (Number(profile?.holding_gold) || 0) + (Number(orderData.weight) || 0) })
              .eq('id', user.id);
          }
        }
      }

      const { error } = await supabase
        .from('payments')
        .update({ status: 'paid', payment_method: '余额支付', expire_time: null })
        .eq('id', selectedPayment);

      if (error) {
        showToast('支付失败，请重试', 'error');
        return;
      }
      setShowPayModal(false);
      setSelectedPayment(null);
      showToast('支付成功，黄金已存入您的金库', 'success');
      fetchData();
    } catch (err) {
      console.error('[handlePay] 支付异常:', err);
      showToast('支付失败，请重试', 'error');
    } finally {
      setPaying(false);
    }
  };

  const allPaymentItems = paymentItems;

  const filteredPayments = allPaymentItems;

  const pendingTotal = allPaymentItems.reduce((sum, p) => sum + p.amount, 0);
  const pendingCount = allPaymentItems.length;

  const animatedPendingTotal = useCountUp(pendingTotal);
  const animatedPendingCount = useCountUp(pendingCount);

  const paymentDetail = allPaymentItems.find((p) => p.id === selectedPayment);

  return (
    <div className="min-h-screen bg-brown-50 pb-28">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-brown-950 px-5 py-4">
        <h1 className="text-gold-400 text-lg font-bold text-center">履约支付</h1>
      </div>

      {/* Stats Dashboard */}
      <div className="bg-gradient-to-br from-brown-900 via-brown-900 to-brown-800 px-4 pt-5 pb-6">
        <div className="grid grid-cols-2 gap-3">
          {/* Pending Amount Card */}
          <div className="relative overflow-hidden rounded-xl bg-brown-800/60 border border-gold-500/10 p-3 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-gold-500/5 to-transparent"></div>
            <div className="relative">
              <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-gold-500/10 flex items-center justify-center">
                <i className="ri-money-cny-circle-line text-gold-400 text-sm"></i>
              </div>
              <p className="text-gold-300 text-xl font-bold leading-none">
                ¥{animatedPendingTotal.toLocaleString()}
              </p>
              <p className="text-gold-500/50 text-[11px] mt-1.5">待支付总额</p>
            </div>
          </div>
          {/* Pending Count Card */}
          <div className="relative overflow-hidden rounded-xl bg-brown-800/60 border border-gold-500/10 p-3 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-gold-500/5 to-transparent"></div>
            <div className="relative">
              <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-gold-500/10 flex items-center justify-center">
                <i className="ri-file-list-3-line text-gold-400 text-sm"></i>
              </div>
              <p className="text-gold-300 text-2xl font-bold leading-none">{animatedPendingCount}</p>
              <p className="text-gold-500/50 text-[11px] mt-1.5">待支付笔数</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white px-4 py-3 border-b border-brown-100/80">
        <div className="inline-flex bg-brown-50 rounded-full p-1 gap-0">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className="relative px-5 py-2 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap text-brown-950"
            >
              <span className="absolute inset-0 bg-white rounded-full shadow-sm border border-brown-100"></span>
              <span className="relative z-10">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Payment List */}
      <div className="px-4 mt-4 space-y-3">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"></span>
            <p className="text-brown-400 text-xs">加载中...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl p-8 text-center border border-brown-100">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-brown-50 flex items-center justify-center">
              <i className="ri-error-warning-line text-brown-400 text-xl"></i>
            </div>
            <p className="text-brown-500 text-sm mb-3">支付数据加载失败</p>
            <button
              onClick={fetchData}
              className="px-5 py-2.5 rounded-full bg-gold-500 text-brown-950 text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-gold-600 transition-colors"
            >
              重新加载
            </button>
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-brown-100">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-brown-50 flex items-center justify-center">
              <i className="ri-inbox-line text-brown-300 text-2xl"></i>
            </div>
            <p className="text-brown-400 text-sm">暂无支付记录</p>
            <p className="text-brown-300 text-xs mt-1">锁价订单确认后将自动生成支付单</p>
          </div>
        ) : (
          filteredPayments.map((item) => {
            const status = statusMap[item.statusCode] || statusMap.pending;
            return (
              <div
                key={item.id}
                className="bg-white rounded-xl border border-brown-100 overflow-hidden"
              >
                {/* Card Header */}
                <div className="px-4 pt-4 pb-3">
                  <div className="flex items-center justify-between mb-2.5">
                    <div className="flex items-center gap-2">
                      <span className="text-brown-400 text-[11px] font-mono">{item.id}</span>
                      <button
                        onClick={() => {
                          navigator.clipboard?.writeText(item.id);
                          showToast('单号已复制', 'success');
                        }}
                        className="w-5 h-5 flex items-center justify-center rounded bg-brown-50 text-brown-400 cursor-pointer hover:bg-brown-100 transition-colors"
                        title="复制单号"
                      >
                        <i className="ri-file-copy-line text-[10px]"></i>
                      </button>
                    </div>
                    <div className={`flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${status.bg}`}>
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className={status.icon}></i>
                      </span>
                      <span className={`font-medium ${status.color}`}>{status.label}</span>
                    </div>
                  </div>
                  <h4 className="text-brown-900 text-sm font-bold">{item.orderName || item.product}</h4>
                  {item.weight > 0 && (
                    <p className="text-brown-400 text-xs mt-0.5">
                      {item.direction === 'IN' ? '买入' : '卖出'} {item.weight}g
                    </p>
                  )}
                </div>

                {/* Price & Info Banner */}
                <div className="px-4 pb-3">
                  <div className="bg-brown-50 rounded-lg px-3 py-2.5 border border-brown-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-time-line text-brown-400 text-xs"></i>
                        </div>
                        <span className="text-brown-400 text-xs">{item.createTime}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-gold-600 text-lg font-bold">
                          ¥{item.lockPrice.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span className="text-brown-400 text-[10px] ml-1">/克（锁价）</span>
                      </div>
                    </div>
                    {item.totalAmount > 0 && item.marginPaid > 0 && (
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-brown-100/60">
                        <span className="text-brown-400 text-xs">
                          预约总额 ¥{item.totalAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                        </span>
                        <span className="text-brown-400 text-xs">
                          已锁定保证金 ¥{item.marginPaid.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Actions */}
                <div className="px-4 pb-4">
                  <button
                    onClick={() => {
                      setSelectedPayment(item.id);
                      setShowPayModal(true);
                    }}
                    className="w-full py-2.5 rounded-xl bg-gold-500 text-brown-950 text-sm font-bold cursor-pointer whitespace-nowrap hover:bg-gold-600 transition-colors flex items-center justify-center gap-1.5"
                  >
                    <i className="ri-wallet-3-line text-sm"></i>
                    补齐全款
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Payment Modal - Bottom Sheet */}
      {showPayModal && paymentDetail && (
        <div className="fixed inset-0 z-[60] flex items-end">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowPayModal(false)}
          ></div>
          <div className="relative bg-white rounded-t-2xl w-full max-h-[85vh] overflow-auto sheet-slide-up pb-24">
            {/* Modal Handle */}
            <div className="sticky top-0 bg-white rounded-t-2xl z-10 px-4 pt-3 pb-0">
              <div className="w-10 h-1 bg-brown-200 rounded-full mx-auto mb-3"></div>
            </div>

            {/* Modal Header */}
            <div className="px-4 pb-4 border-b border-brown-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-brown-900 text-base font-bold">确认支付</h3>
                <button
                  onClick={() => setShowPayModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-brown-50 text-brown-400 cursor-pointer hover:bg-brown-100 transition-colors"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-brown-500 text-xs font-mono">{paymentDetail.id}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${statusMap[paymentDetail.statusCode].bg} ${statusMap[paymentDetail.statusCode].color} font-medium`}>
                  {statusMap[paymentDetail.statusCode].label}
                </span>
              </div>
            </div>

            {/* Product Info */}
            <div className="px-4 py-4">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold-400/20 to-gold-600/10 flex items-center justify-center">
                  <i className="ri-vip-crown-line text-gold-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-brown-900 text-sm font-bold">{paymentDetail.orderName || paymentDetail.product}</p>
                  {paymentDetail.weight > 0 && (
                    <p className="text-brown-400 text-xs mt-0.5">
                      {paymentDetail.direction === 'IN' ? '买入' : '卖出'} {paymentDetail.weight}g · 锁价 ¥{paymentDetail.lockPrice.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/克
                    </p>
                  )}
                </div>
              </div>

              {/* Amount */}
              <div className="text-center mb-5 p-4 bg-brown-50 rounded-xl border border-brown-100">
                <p className="text-brown-400 text-xs mb-1">需补齐全款（尾款）</p>
                <p className="text-gold-600 text-3xl font-bold">
                  ¥{paymentDetail.amount.toLocaleString()}
                </p>
                {paymentDetail.totalAmount > 0 && paymentDetail.marginPaid > 0 && (
                  <div className="flex items-center justify-center gap-3 mt-2">
                    <span className="text-brown-400 text-xs">
                      预约总额 ¥{paymentDetail.totalAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                    </span>
                    <span className="text-brown-300 text-xs">|</span>
                    <span className="text-brown-400 text-xs">
                      已付定金 ¥{paymentDetail.marginPaid.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </div>

              {/* Payment Methods */}
              <div className="space-y-2 mb-5">
                <button
                  onClick={handlePay}
                  disabled={paying}
                  className="w-full py-3.5 rounded-xl bg-gold-500 text-brown-950 text-sm font-bold flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap hover:bg-gold-600 transition-colors disabled:opacity-60"
                >
                  {paying ? (
                    <span className="w-4 h-4 border-2 border-brown-950/30 border-t-brown-950 rounded-full animate-spin"></span>
                  ) : (
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-wallet-3-line"></i>
                    </span>
                  )}
                  余额补齐全款
                </button>
              </div>

              <button
                onClick={() => setShowPayModal(false)}
                className="w-full py-3 rounded-xl border border-brown-200 text-brown-500 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-brown-50 transition-colors"
              >
                取消支付
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}